import { parseISO } from 'date-fns';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { handleOnSettled } from '@utils/formHelpers';
import { useCreateInternalSchedule } from '@services/schedules/internal/create';
import { ScheduleNote } from '@services/schedules/external/types';
import { useSchedulerContext } from '@contexts/scheduler';
import { initialState } from '@contexts/scheduler/constants';
import { useAppContext } from '@contexts/app';
import { InternalScheduleRespSuccess } from '@services/schedules/internal/types';
import { FormProps } from '@forms/types';
import { InternalScheduleFormProps, InternalScheduleFormValues } from './types';
import { defaultValues, formSchema } from './constants';
import { useErrors } from '@hooks/errors';
import { camelToSnakeCase } from '@utils/textHelpers';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { useRescheduleInternalSchedule } from '@services/schedules/internal/reschedule';
import { useUpdateInternalScheduleCrewMembers } from '@services/schedules/internal/update';
import { useMemo, useRef } from 'react';
import {
  getExtensionDates,
  isSameScheduleDay,
  isScheduleDataUnchanged,
  parseMultiDaySchedules,
  parseSchedulesForDates,
  parseScheduleEquipmentAttributesToRequest,
  parseScheduleMembersAttributesToRequest,
} from './helpers';
import {
  isExistingMultiDay,
  mapResponseScheduleEquipmentToForm,
  mapResponseScheduleMembersToForm,
} from '@contexts/scheduler/sidebar/helpers';
import { Conflict } from '@components/schedules/dialogs/internal/publish/types';
import { useCreateInternalMultischedule } from '@services/schedules/internal/multischedules/create';
import { CreateInternalScheduleMultiDayReq } from '@services/schedules/internal/multischedules/types';

export const useInternalScheduleForm = ({
  successHandler,
  onOverlap,
  onSpecialtyOverride,
  schedule: initialSchedule,
}: FormProps<InternalScheduleFormProps, InternalScheduleRespSuccess>) => {
  const { tenant } = useAppContext();
  const { setState, active } = useSchedulerContext();
  const {
    currentSchedule,
    crewChangeScope: selectedCrewChangeScope,
    hasScheduleChanges,
    ...sidebarState
  } = useSchedulerSidebar();
  const crewChangeScope = hasScheduleChanges ? selectedCrewChangeScope : 'currentOnly';

  const handleErrors = useErrors();

  const schedule = initialSchedule || active?.schedule;

  const currentScheduleMembers = (initialSchedule || currentSchedule)?.scheduleMembers || [];
  const currentScheduleEquipment = (initialSchedule || currentSchedule)?.scheduleAssets || [];
  const currentScheduleRequirements =
    (initialSchedule || currentSchedule)?.task?.requirements || [];
  const crewMembers = initialSchedule
    ? mapResponseScheduleMembersToForm(initialSchedule.scheduleMembers || [])
    : sidebarState.crewMembers;
  const equipmentMembers = initialSchedule
    ? mapResponseScheduleEquipmentToForm(initialSchedule.scheduleAssets || [])
    : sidebarState.equipmentMembers;
  const defaultCrew = (initialSchedule || sidebarState).defaultCrew;
  const defaultCrewChanged = (initialSchedule || sidebarState).defaultCrewChanged;
  const defaultEquipment = (initialSchedule || sidebarState).defaultEquipment;
  const defaultEquipmentChanged = (initialSchedule || sidebarState).defaultEquipmentChanged;

  const initialScheduleMembers = useMemo(
    () => mapResponseScheduleMembersToForm(currentScheduleMembers),
    [currentScheduleMembers, initialSchedule, currentSchedule],
  );

  const initialScheduleEquipmentMembers = useMemo(
    () => mapResponseScheduleEquipmentToForm(currentScheduleEquipment),
    [currentScheduleEquipment],
  );

  const form = useForm<InternalScheduleFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues,
  });

  const dayMoved = useRef(true);

  const callback = (response: InternalScheduleRespSuccess) => {
    const resp = dayMoved.current ? response : { ...response, relatedSchedules: [] };
    const err = resp.errors as unknown as { base: string[]; conflicts?: Array<Conflict[]> };
    if (err?.base?.includes('overlapping_schedules')) {
      onOverlap(err.conflicts?.[0] as Conflict[]);
    } else {
      if (setState) setState(initialState);
      handleOnSettled<InternalScheduleRespSuccess>(
        resp,
        successHandler,
        () =>
          handleErrors(resp.errors, (key, message) =>
            form.setError(key as keyof InternalScheduleFormValues, { message }),
          ),
        true,
      );
    }
  };

  const create = useCreateInternalSchedule({
    id: schedule.task?.job?.id || '',
    callback,
  });
  const reschedule = useRescheduleInternalSchedule({
    id: schedule.task?.job?.id || '',
    callback,
  });
  const updateCrewMembers = useUpdateInternalScheduleCrewMembers({ callback });
  const createMultiDay = useCreateInternalMultischedule({
    callback: (resp) => callback(resp as InternalScheduleRespSuccess),
  });

  const onSubmit = async (values: InternalScheduleFormValues) => {
    const {
      note: content,
      confirmed,
      forceSpecialty,
      actionType,
      status,
      days,
      addDays,
      skipSaturday,
      skipSunday,
      pickedDates,
      ...rest
    } = values;
    if (!forceSpecialty && onSpecialtyOverride && schedule.foreman?.id) {
      onSpecialtyOverride();
      return;
    }
    const [startsAt, endsAt] = [
      values.startsAt || schedule.startsAt,
      values.endsAt || schedule.endsAt,
    ];

    const noteObject = content
      ? ({
          id: schedule?.task?.note?.id,
          content,
          referableId: schedule?.task?.id,
          referableType: 'Task',
        } as ScheduleNote)
      : undefined;

    const isExistingSchedule = Boolean(schedule.id && schedule.id !== 'new');
    dayMoved.current =
      !isExistingSchedule ||
      !isSameScheduleDay(startsAt, (initialSchedule || currentSchedule)?.startsAt);
    const inSeries =
      Boolean(schedule.task?.multischedule) ||
      isExistingMultiDay(sidebarState.relatedMultischedules);
    const pickedDays = inSeries ? [] : pickedDates.map((day) => parseISO(day));
    const isPickedDays = pickedDays.length > 1;
    const isMultiDay = (days > 1 && !schedule.task?.multischedule) || isPickedDays;
    const extensionDates =
      isExistingSchedule &&
      (schedule.task?.multischedule || isExistingMultiDay(sidebarState.relatedMultischedules))
        ? getExtensionDates({
            relatedMultischedules: sidebarState.relatedMultischedules,
            currentScheduleId: currentSchedule?.id,
            startsAt,
            zone: tenant.zone,
            count: addDays,
            options: { skipSaturday, skipSunday },
          })
        : [];
    const isExtension = extensionDates.length > 0;

    const foremanId = values.relatedScheduleForemanId || schedule?.foreman?.id || '';

    const newScheduleValues = {
      startsAt,
      endsAt,
      foremanId,
      forceOverlapping: rest.forceOverlapping || false,
      noteContent: content || '',
      status,
    };
    const scheduleDataUnchanged =
      isExistingSchedule &&
      !initialSchedule &&
      isScheduleDataUnchanged(newScheduleValues, currentSchedule);

    const scheduleMembersAttributes = parseScheduleMembersAttributesToRequest(
      crewMembers,
      initialScheduleMembers,
    );
    const scheduleAssetsAttributes = parseScheduleEquipmentAttributesToRequest(
      equipmentMembers,
      initialScheduleEquipmentMembers,
    );
    const taskRequirement = currentScheduleRequirements?.[0] || {};

    if (isExistingSchedule && scheduleDataUnchanged && !isMultiDay && !isExtension) {
      updateCrewMembers.mutate({
        scheduleId: schedule.id,
        scheduleMembersAttributes,
        scheduleAssetsAttributes,
        actionType: camelToSnakeCase(actionType),
        defaultCrew: defaultCrew || false,
        forceOverlapping: rest.forceOverlapping || false,
        defaultCrewChanged: defaultCrewChanged || false,
        crewChangeScope: crewChangeScope || 'currentOnly',
        defaultEquipment: defaultEquipment || false,
        defaultEquipmentChanged: defaultEquipmentChanged || false,
        taskRequirement,
        confirmed: Boolean(confirmed),
      });
      return;
    }

    const defaultCrewValue = foremanId ? defaultCrew : false;
    const defaultEquipmentValue = foremanId ? defaultEquipment : false;
    const filteredScheduleMembersAttributes = scheduleMembersAttributes
      .filter((el) => el._destroy !== '1')
      .map((el) => ({ userId: el.userId }));
    const filteredScheduleEquipmentAttributes = scheduleAssetsAttributes
      .filter((el) => el._destroy !== '1')
      .map((el) => ({ assetId: el.assetId }));

    if (isMultiDay || isExtension) {
      const multiDayData: CreateInternalScheduleMultiDayReq = {
        ...(isExistingSchedule && { scheduleId: schedule.id }),
        taskId: schedule.task?.id || '',
        foremanId,
        confirmed: Boolean(confirmed),
        forceOverlapping: rest.forceOverlapping || false,
        note: noteObject,
        schedulesAttributes: (isExtension
          ? [
              { startsAt, endsAt },
              ...parseSchedulesForDates(startsAt, endsAt, extensionDates, tenant.zone),
            ]
          : isPickedDays
            ? parseSchedulesForDates(startsAt, endsAt, pickedDays, tenant.zone)
            : parseMultiDaySchedules(startsAt, endsAt, days, { skipSaturday, skipSunday })
        ).map((el) => ({ ...el, fullDay: false, note: {} })),
        defaultCrew: defaultCrewValue,
        crewChangeScope: crewChangeScope || 'currentOnly',
        scheduleMembersAttributes: filteredScheduleMembersAttributes,
        scheduleAssetsAttributes: filteredScheduleEquipmentAttributes,
        taskRequirement,
        defaultEquipment: defaultEquipmentValue,
        ...(isExistingSchedule && {
          defaultCrewChanged: defaultCrewChanged || false,
          defaultEquipmentChanged: defaultEquipmentChanged || false,
        }),
        status: status === 'draft' ? 'draft' : '',
        actionType: camelToSnakeCase(actionType),
      };
      createMultiDay.mutate(multiDayData);
      return;
    }

    const data = {
      ...(isExistingSchedule && { scheduleId: schedule.id }),
      schedule: {
        ...rest,
        taskId: schedule.task?.id || '',
        startsAt,
        endsAt,
        foremanId,
        confirmed: Boolean(confirmed),
        defaultCrew: defaultCrewValue,
        crewChangeScope: crewChangeScope || 'currentOnly',
        scheduleMembersAttributes: filteredScheduleMembersAttributes,
        scheduleAssetsAttributes: filteredScheduleEquipmentAttributes,
        taskRequirement,
        ...(isExistingSchedule
          ? {
              defaultCrewChanged: defaultCrewChanged || false,
              defaultEquipmentChanged: defaultEquipmentChanged || false,
            }
          : {}),
        defaultEquipment: defaultEquipmentValue,
        status: status === 'draft' ? 'draft' : '',
      },
      actionType: camelToSnakeCase(actionType),
      note: noteObject,
    };

    if (isExistingSchedule) {
      reschedule.mutate(data);
    } else {
      create.mutate(data);
    }
  };

  return {
    form,
    onSubmit,
    locked:
      create.isLoading ||
      reschedule.isLoading ||
      updateCrewMembers.isLoading ||
      createMultiDay.isLoading,
  };
};
